import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnChanges, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

import { setLeftColumnVisible } from '@offconon/shared/features/layout';
import { ThemeService } from '@offconon/shared/ui/theme-switch';
import { RbacHelperService } from '@offconon/shared/utils/services/rbac-helper';

@Component({
  selector: 'offconon-admin-module-menu',
  imports: [
    CommonModule,
    RouterModule,
    MatIcon,
    TranslateModule,
    ButtonModule,
    RouterModule,
    TooltipModule,
  ],
  templateUrl: './admin-module-menu.component.html',
  styles: ``,
})
export class AdminModuleMenuComponent implements OnInit, OnChanges {
  @Input() activepage: any;
  @Input() app_enum: any;

  private rbacHelperService = inject(RbacHelperService);
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private store = inject(Store);

  moduleMenuItems: any;
  mobileView = toSignal(inject(ThemeService).mobileView);

  module_name: any;
  module_active: any;
  homelink = '/';

  ngOnChanges(): void {
    this.createHeaderMenu(this.activepage, this.app_enum);
  }

  ngOnInit() {
    this.createHeaderMenu(this.activepage, this.app_enum);
  }

  back() {
    window.history.back();
  }

  commandSelector(command: any) {
    switch (command) {
      case 'command': {
        //statements redirect to selected command;
        break;
      }
    }
  }

  openSubPage(url: any) {
    this.router.navigate([url]);
  }

  createHeaderMenu(activepage: any, app_enum: any) {
    switch (app_enum) {
      case 'apps': {
        this.appsAppMenu(activepage);
        break;
      }
      case 'category': {
        this.categoryMenu(activepage);
        break;
      }
      case 'custom_fields': {
        this.customFieldsMenu(activepage);
        break;
      }
      case 'catalog': {
        this.catalogMenu(activepage);
        break;
      }
      case 'locations': {
        this.locationsMenu(activepage);
        break;
      }

      case 'website': {
        this.websiteMenu(activepage);
        break;
      }

      case 'finance': {
        this.financeMenu(activepage);
        break;
      }
      case 'rbac': {
        this.rbacMenu(activepage);
        break;
      }
    }
  }

  rbacMenu(activepage: any) {
    let moduleEnumList: any;
    moduleEnumList = this.rbacHelperService.getModuleEnums();

    this.moduleMenuItems = [
      {
        label: this.translateService.instant('Role-based access control (RBAC)'),
        items: [
          {
            link: '/rbac-admin',
            icon: 'call_to_action',
            label: 'Access rule patterns',
          },
        ],
      },

      {
        label: this.translateService.instant('Module pattern options'),
        items: moduleEnumList
          ? [
              ...moduleEnumList.map((moduleItem: any) => {
                return {
                  icon: moduleItem.moduleIcon,
                  label: this.rbacHelperService.getRuleTypeModulename(moduleItem.moduleEnum),
                  link:
                    '/rbac-admin/access-pattern/' +
                    this.rbacHelperService.getRuleTypeLink(moduleItem.moduleEnum),
                };
              }),
            ]
          : [],
      },

      {
        label: this.translateService.instant('Create pattern option'),
        items: moduleEnumList
          ? [
              ...moduleEnumList.map((moduleItem: any) => {
                return {
                  icon: 'library_add',
                  label: this.rbacHelperService.getRuleTypeModulename(moduleItem.moduleEnum),
                  subpage: moduleItem.moduleEnum + '_create',
                  module: 'rbac_' + moduleItem.moduleEnum,
                  link:
                    '/rbac-admin/create-access-pattern/' +
                    this.rbacHelperService.getRuleTypeLink(moduleItem.moduleEnum),
                };
              }),
            ]
          : [],
      },
    ];

    this.module_active = activepage;
  }

  financeMenu(activepage: any) {
    this.moduleMenuItems = [
      {
        label: this.translateService.instant('Bank database management'),
        items: [
          {
            link: '/bank-database-management',
            icon: 'folder_copy',
            label: 'Banks',
          },
          {
            link: '/bank-database-management/create-bank',
            icon: 'library_add',
            label: 'Add bank to our system',
          },
        ],
      },
      {
        label: this.translateService.instant('Payment providers'),
        items: [
          {
            link: '/payment-providers-management',
            icon: 'compare',
            label: 'Payment providers',
          },
          {
            link: '/payment-providers-management/add-payment-provider',
            icon: 'library_add',
            label: 'Add payment provider our system',
          },
        ],
      },
      {
        label: this.translateService.instant('Payment options on the spot'),
        items: [
          {
            link: '/payment-on-spot-management',
            icon: 'storefont',
            label: 'Payment options on the spot',
          },
          {
            link: '/payment-on-spot-management/add-payment-on-spot',
            icon: 'library_add',
            label: 'Add a new on-site payment method to our system',
          },
        ],
      },
    ];

    this.module_active = activepage;
  }

  websiteMenu(activepage: any) {
    this.moduleMenuItems = [
      {
        label: this.translateService.instant('Website / Application manager'),
        items: [
          {
            link: '/website-preview/site-preview',
            icon: 'call_to_action',
            label: 'Sites',
          },
          {
            link: '/website-preview/page-preview',
            icon: 'description',
            label: 'Pages',
          },
          {
            link: '/website-preview/widget-preview',
            icon: 'category',
            label: 'Component templates',
          },
        ],
      },
    ];

    this.module_active = activepage;
  }

  appsAppMenu(activepage: any) {
    this.moduleMenuItems = [
      {
        label: this.translateService.instant('Modules'),
        items: [
          {
            link: '/applications-admin',
            icon: 'dashboard',
            label: 'Applications',
          },

          {
            link: '/academy-admin',
            icon: 'dashboard',
            label: 'Academy',
          },

          {
            link: '/languages-admin',
            icon: 'dashboard',
            label: 'Languages',
          },

          {
            link: '/mr-ai-code-admin',
            icon: 'dashboard',
            label: 'MR AI Code',
          },

          {
            link: '/si-units-admin',
            icon: 'dashboard',
            label: 'International System of Units',
          },

          {
            link: '/timezone-admin',
            icon: 'dashboard',
            label: 'Timezone',
          },

          {
            link: '/legal-entity-types-admin',
            icon: 'dashboard',
            label: 'Legal entity types',
          },

          {
            link: '/system-email-templates-admin',
            icon: 'dashboard',
            label: 'Email templates',
          },
          {
            link: '/crm-targeted-customers',
            icon: 'dashboard',
            label: 'CRM database of targeted potential customers',
          },
        ],
      },
    ];

    this.module_active = activepage;
  }

  categoryMenu(activepage: any) {
    this.moduleMenuItems = [
      {
        label: this.translateService.instant('Ad categories'),
        items: [
          {
            link: '/category-template-admin/physical-product/list',
            icon: 'dashboard',
            label: 'Physical product',
          },
          {
            link: '/category-template-admin/service-offered/list',
            icon: 'dashboard',
            label: 'Service template',
          },
          {
            link: '/category-template-admin/rentable-product/list',
            icon: 'dashboard',
            label: 'Rentable product',
          },
          {
            link: '/category-template-admin/digital-product/list',
            icon: 'dashboard',
            label: 'Digital product',
          },
          {
            link: '/category-template-admin/digital-media/list',
            icon: 'dashboard',
            label: 'All digital media (Video / Podcast / Magazine)',
          },

          {
            link: '/category-template-admin/job/list',
            icon: 'dashboard',
            label: 'Job',
          },

          {
            link: '/category-template-admin/booking/list',
            icon: 'dashboard',
            label: 'Booking',
          },
          {
            link: '/category-template-admin/knowledge-academy/list',
            icon: 'dashboard',
            label: 'Knowledge academy',
          },
        ],
      },
      {
        label: this.translateService.instant('Catalog categories'),
        items: [
          {
            link: '/category-template-admin/bucket-list/list',
            icon: 'dashboard',
            label: 'Bucket list',
          },

          {
            link: '/category-template-admin/plant/list',
            icon: 'dashboard',
            label: 'Plant catalog',
          },
          {
            link: '/category-template-admin/animal/list',
            icon: 'dashboard',
            label: 'Animal catalog',
          },
          {
            link: '/category-template-admin/food/list',
            icon: 'dashboard',
            label: 'Food catalog',
          },
          {
            link: '/category-template-admin/drink/list',
            icon: 'dashboard',
            label: 'Drink catalog',
          },
          {
            link: '/category-template-admin/cooking-recepie/list',
            icon: 'dashboard',
            label: 'Cooking recepie catalog',
          },
          {
            link: '/category-template-admin/food-component/list',
            icon: 'dashboard',
            label: 'Food component catalog',
          },
        ],
      },
      {
        label: this.translateService.instant('Helper categories'),
        items: [
          {
            link: '/category-template-admin/custom-field/list',
            icon: 'dashboard',
            label: 'Custom filed',
          },
          {
            link: '/category-template-admin/application/list',
            icon: 'dashboard',
            label: 'Application',
          },
        ],
      },

      {
        label: this.translateService.instant('Website editor categories'),
        items: [
          {
            link: '/category-template-admin/webpage-editor-website/list',
            icon: 'dashboard',
            label: 'Website editor website',
          },
          {
            link: '/category-template-admin/webpage-editor-page/list',
            icon: 'dashboard',
            label: 'Website editor page',
          },

          {
            link: '/category-template-admin/webpage-editor-widget/list',
            icon: 'dashboard',
            label: 'Website editor widget',
          },
        ],
      },
    ];

    this.module_active = activepage;
  }

  customFieldsMenu(activepage: any) {
    this.moduleMenuItems = [
      {
        label: this.translateService.instant('Modules'),
        items: [
          {
            link: '/custom-fields',
            icon: 'dashboard',
            label: 'Custom fields',
          },
        ],
      },

      {
        label: this.translateService.instant('Help'),
        items: [
          {
            link: '/academy',
            icon: 'cast_for_education',
            label: 'Academy',
          },
        ],
      },
    ];

    this.module_active = activepage;
  }

  catalogMenu(activepage: any) {
    this.moduleMenuItems = [
      {
        label: this.translateService.instant('Catalog'),
        items: [
          {
            link: '/catalog/plant',
            icon: 'yard',
            label: 'Plant catalog',
          },
          {
            link: '/catalog/animal',
            icon: 'pets',
            label: 'Animal catalog',
          },

          {
            link: '/catalog/physical',
            icon: 'pallet',
            label: 'Physical product catalog',
          },

          {
            link: '/catalog/food',
            icon: 'restaurant',
            label: 'Food catalog',
          },

          {
            link: '/catalog/bucket',
            icon: 'checklist',
            label: 'Bucket list catalog',
          },
          {
            link: '/catalog/foodcomponent',
            icon: 'restaurant',
            label: 'Food component',
          },

          {
            link: '/catalog/drink',
            icon: 'restaurant',
            label: 'Drink catalog',
          },

          {
            link: '/catalog/service',
            icon: 'restaurant',
            label: 'Service template',
          },
          {
            link: '/catalog/bulk-create',
            icon: 'dashboard',
            label: 'Bulk Create',
          },
        ],
      },

      {
        label: this.translateService.instant('Help'),
        items: [
          {
            link: '/academy',
            icon: 'cast_for_education',
            label: 'Academy',
          },
        ],
      },
    ];

    this.module_active = activepage;
  }

  locationsMenu(activepage: any) {
    this.moduleMenuItems = [
      {
        label: this.translateService.instant('Catalog'),
        items: [
          {
            link: '/locations/regions',
            icon: 'dashboard',
            label: 'Regions',
          },
          {
            link: '/locations/sub-regions',
            icon: 'dashboard',
            label: 'Subregions',
          },
          {
            link: '/locations/countries',
            icon: 'dashboard',
            label: 'Countries',
          },
          {
            link: '/locations/provinces',
            icon: 'dashboard',
            label: 'Provinces',
          },
        ],
      },

      {
        label: this.translateService.instant('Help'),
        items: [
          {
            link: '/academy',
            icon: 'cast_for_education',
            label: 'Academy',
          },
        ],
      },
    ];

    this.module_active = activepage;
  }

  toggleSideBar(state = false) {
    this.store.dispatch(setLeftColumnVisible({ leftColumnVisible: state }));
  }
  closeMenu() {
    if (this.mobileView()) this.store.dispatch(setLeftColumnVisible({ leftColumnVisible: false }));
  }
}
